import { useState } from 'react';
import { ChevronDown, Menu, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import LocalLink from "@/components/ui/LocalLink";
import { AnimatedText, AnimatedTitle } from '../components/ui/AnimatedText';
import { menuData } from '../utils/menuData';
import { useTranslation } from "react-i18next";
import { useSEO } from "@/utils/useSEO";

const RestaurantMenu = () => {
  const { t, i18n } = useTranslation();
  useSEO("Restaurant Menu", "Indulge in exquisite culinary creations that nourish the body, accompanied by the majestic panorama of Mount Agung.");
  const [activeCategory, setActiveCategory] = useState(menuData[0].title);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleCategoryChange = (title: string) => {
    setActiveCategory(title);
    window.scrollTo({
      top: window.innerHeight * 0.5,
      behavior: 'smooth'
    });
  };

  const scrollToMenu = () => {
    const element = document.getElementById('menu-section');
    if (element) {
      window.scrollTo({
        top: element.getBoundingClientRect().top + window.scrollY - 80,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="min-h-[100dvh] bg-background text-white selection:bg-gold/30 selection:text-gold font-sans flex flex-col">

      {/* Back to Home Button */}
      <LocalLink 
        to={i18n.language === 'en' || !i18n.language ? '/' : `/${i18n.language}`} 
        className="fixed bottom-6 left-6 z-[100] flex items-center gap-2 bg-black/80 hover:bg-black backdrop-blur-md text-white/90 px-5 py-3 rounded-full border border-white/20 shadow-xl transition-all text-sm font-semibold uppercase tracking-wider shadow-black/50"
      >
        <ArrowLeft size={16} />
        {t("restaurant.backToHome", "Back to Home")}
      </LocalLink>

      {/* Hero Section */}
      <section className="relative min-h-[100dvh] w-full flex items-center justify-center">
        <div className="absolute inset-0">
          <img
            src="/media/patal-kikian-hero-banner.webp"
            alt="Patal Kikian Restaurant"
            className="w-full h-full object-cover opacity-85"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/30 to-transparent" />
        </div>
        
        <div className="relative z-10 text-center px-4">
          <AnimatedText delay={0.1}>
            <span className="text-gold uppercase tracking-[0.2em] text-xs font-semibold mb-4 block">{t("restaurant.hero.subtitle", "Leaf Forest Restaurant")}</span>
          </AnimatedText>
          <AnimatedTitle title={t("restaurant.hero.title", "A Soul-Soothing Culinary Journey")} className="text-4xl md:text-5xl lg:text-6xl font-luxury mb-6 text-shadow-lg leading-tight" />
          <AnimatedText delay={0.3}>
            <p className="text-white/80 max-w-2xl mx-auto text-lg leading-relaxed mb-8">
              {t("restaurant.hero.desc", "Indulge in exquisite culinary creations that nourish the body, accompanied by the majestic panorama of Mount Agung and the lush green valleys of Sidemen—a setting designed to calm the heart and refresh the mind.")}
            </p>
          </AnimatedText>
          <AnimatedText delay={0.4}>
            <a 
              href="/document/Leaf Forest Menu.pdf" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-gold text-black hover:bg-white hover:text-black transition-colors px-6 py-3 rounded-full text-sm font-semibold uppercase tracking-widest shadow-lg shadow-gold/20"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              {t("restaurant.downloadPdf", "Download PDF Menu")}
            </a>
          </AnimatedText>
        </div>

        {/* Scroll Down Button */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-20">
          <button 
            onClick={scrollToMenu}
            className="text-white/70 hover:text-white transition-colors duration-300 flex flex-col items-center gap-2 cursor-pointer"
            aria-label="Scroll to menu"
          >
            <span className="uppercase tracking-[0.2em] text-[10px]">{t("restaurant.hero.btnScroll", "View Menu")}</span>
            <ChevronDown size={24} className="animate-bounce" />
          </button>
        </div>
      </section>

      {/* Main Menu Section */}
      <section id="menu-section" className="py-16 md:py-24 bg-background relative z-20 flex-grow">
        <div className="container mx-auto px-4 md:px-8 max-w-6xl flex flex-col md:flex-row gap-12 relative">
          
          {/* Sidebar Navigation (Desktop) */}
          <div className="md:w-1/4 md:sticky md:top-32 self-start hidden md:block">
            <h3 className="text-xl font-luxury mb-6 text-white border-b border-white/10 pb-4">{t("restaurant.sidebar.categories", "Categories")}</h3>
            <ul className="space-y-3">
              {menuData.map((category) => (
                <li key={category.title}>
                  <button
                    onClick={() => handleCategoryChange(category.title)}
                    className={`text-left w-full transition-colors duration-300 ${
                      activeCategory === category.title ? 'text-gold font-medium' : 'text-white/60 hover:text-white'
                    }`}
                  >
                    {t(`restaurant.categories.${category.title}`, { defaultValue: category.title })}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Mobile Category Dropdown */}
          <div className="md:hidden sticky top-24 z-40 bg-background/95 backdrop-blur-md border-b border-white/10 -mx-4 px-4 py-3">
            <button 
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="w-full flex justify-between items-center text-white text-sm uppercase tracking-wider py-2"
            >
              <span>More Menu</span>
              <Menu className="w-5 h-5 text-gold" />
            </button>
            {isMobileMenuOpen && (
              <div className="flex flex-col gap-4 mt-4 max-h-[50vh] overflow-y-auto pb-4 custom-scrollbar">
                {menuData.map((category) => (
                  <button
                    key={category.title}
                    onClick={() => { handleCategoryChange(category.title); setIsMobileMenuOpen(false); }}
                    className={`text-left text-sm uppercase tracking-wider transition-colors duration-300 ${
                      activeCategory === category.title ? 'text-gold' : 'text-white/60'
                    }`}
                  >
                    {t(`restaurant.categories.${category.title}`, { defaultValue: category.title })}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Menu Content (Only active category) */}
          <div className="md:w-3/4">
            {menuData.filter(c => c.title === activeCategory).map((category) => (
              <div key={category.title} className="mb-20 animate-fade-in">
                <div className="mb-10 text-center md:text-left">
                  <h2 className="text-3xl md:text-4xl font-luxury text-gold mb-2">{t(`restaurant.categories.${category.title}`, { defaultValue: category.title })}</h2>
                  {category.subtitle && (
                    <p className="text-white/60 uppercase tracking-widest text-sm">{t(`restaurant.categories.${category.title}_subtitle`, { defaultValue: category.subtitle })}</p>
                  )}
                  <div className="w-16 h-px bg-gold/50 mx-auto md:mx-0 mt-6" />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
                  {category.items.map((item, idx) => (
                    <div key={idx} className="group">
                      <div className="flex justify-between items-baseline mb-2 gap-4">
                        <h4 className="text-lg font-medium text-white group-hover:text-gold transition-colors duration-300 flex-1">
                          {item.name}
                          {item.isBestSeller && (
                            <span className="ml-2 inline-flex items-center justify-center bg-gold/20 text-gold text-[10px] px-2 py-0.5 rounded-sm uppercase tracking-wider align-middle border border-gold/30">
                              {t("restaurant.bestSeller", "Best Seller")}
                            </span>
                          )}
                        </h4>
                        <span className="text-gold font-medium shrink-0">{item.price}</span>
                      </div>
                      
                      {item.description && (
                        <p className="text-white/60 text-sm leading-relaxed mb-3">{item.description}</p>
                      )}

                      {item.tags && item.tags.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {item.tags.map(tag => (
                            <span key={tag} className="text-[10px] uppercase tracking-wider px-2 py-1 border border-white/20 text-white/50 rounded-sm">
                              {t(`restaurant.tags.${tag}`, { defaultValue: tag })}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}

            <div className="mt-16 pt-8 border-t border-white/10 text-center md:text-left">
              <p className="text-white/40 text-sm italic">
                {t("restaurant.taxNote", "* All price subject 11% government tax and 10% service")}
              </p>
              <div className="flex flex-wrap justify-center md:justify-start gap-4 mt-6 text-xs text-white/50">
                <span>{t("restaurant.legend.V", "V = Vegan")}</span>
                <span>{t("restaurant.legend.VG", "VG = Vegetarian")}</span>
                <span>{t("restaurant.legend.GF", "GF = Gluten Free")}</span>
                <span>{t("restaurant.legend.DF", "DF = Dairy Free")}</span>
                <span>{t("restaurant.legend.CN", "CN = Contains Nut")}</span>
              </div>
            </div>
          </div>

        </div>
      </section>
      
    </div>
  );
};

export default RestaurantMenu;
